import { useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { convertCelsiusToFahrenheit } from 'shared/helpers/helpers';

const round1 = (v) => Math.round(v * 10) / 10;




const graphOptions = (source) => [
  { value: 'realtime', label: 'Heater Temp. Vs Real Time' },
  { value: 'heater', label: 'Heater Temp. Vs Time' },
  { value: 'energy', label: `${source === 'gas' ? 'Gas' : 'Energy'} Consumption Vs Time` },
  { value: 'data', label: 'Data Consumption Vs Time' },
];

const BAR_TYPES = ['energy', 'data'];



const PLOT = { left: 12, right: 100, top: 4, bottom: 86 };
const plotW = PLOT.right - PLOT.left;
const plotH = PLOT.bottom - PLOT.top;
const GRAPH_GAP_MS = 900000;

const formatDateAxis = (intervalUnit) => {
  switch (intervalUnit) {
    case 'months':
      return 'MMM';
    case 'weeks':
      return 'ww';
    case 'days':
      return 'DD';
    case 'hours':
      return 'HH';
    case 'minutes':
      return 'mm';
    default:
      return 'HH:mm';
  }
};

const SwitchTempDisplay = ({
  series = [],
  setPoint = null,
  currentTemp = null,
  isF = false,
  title,
  systemLabel = 'ESS - ELECTRIC SWITCH SYSTEM',
  realTime = true,
  intervalUnit = '',
  source = 'energy',
  energyBars = {},
  dataBars = {},
  reportClass,
  onSearch,
  onClear,
}) => {
  const [graphType, setGraphType] = useState('realtime');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const isBarGraph = BAR_TYPES.includes(graphType);
  const activeBars = graphType === 'data' ? dataBars : energyBars;

  const { lines, setPointY, yTicks, timeLabels, currentY } = useMemo(() => {
    const toDisplayTemp = (v) =>
      Math.floor(isF ? convertCelsiusToFahrenheit(v) : v);

    const prepared = (series || [])
      .filter((s) => s?.enabled !== false)
      .map((s) => {
        const points = Array.isArray(s.data)
          ? s.data
              .filter((d) => Array.isArray(d) && typeof d[0] === 'number' && typeof d[1] === 'number')
              .sort((a, b) => a[0] - b[0])
          : [];
        if (points.length === 0) return null;

        return {
          key: s.key,
          color: s.color,
          dashed: s.dashed,
          rawPoints: points,
          valuesByTime: points.reduce((obj, [time, value]) => {
            obj[Math.floor(time)] = toDisplayTemp(value);
            return obj;
          }, {}),
        };
      })
      .filter(Boolean);

    const timestamps = [
      ...new Set(
        prepared
          .flatMap((s) => s.rawPoints.map(([time]) => Math.floor(time)))
          .filter((time) => Number.isFinite(time))
      ),
    ].sort((a, b) => a - b);

    if (prepared.length === 0 || timestamps.length === 0) {
      return { lines: [], setPointY: null, yTicks: [], timeLabels: [], currentY: null };
    }

    const filledSeries = prepared.map((s) => {
      let prev = null;
      const points = timestamps.map((time) => {
        const direct = s.valuesByTime[time];
        let value = typeof direct === 'number' ? direct : null;
        if (value == null && prev && time - prev.time < GRAPH_GAP_MS) {
          value = prev.value;
        }
        if (value != null) prev = { time, value };
        return [time, value];
      });
      return { ...s, points };
    });

    const valueList = filledSeries
      .flatMap((s) => s.points.map(([, value]) => value))
      .filter((value) => typeof value === 'number' && Number.isFinite(value));

    if (typeof setPoint === 'number') valueList.push(setPoint);
    if (typeof currentTemp === 'number') valueList.push(currentTemp);

    let minV = Math.min(...valueList);
    let maxV = Math.max(...valueList);
    if (!Number.isFinite(minV) || !Number.isFinite(maxV)) {
      return { lines: [], setPointY: null, yTicks: [], timeLabels: [], currentY: null };
    }

    const sortedTemps = [...new Set(valueList.map((value) => Math.floor(value)))].sort((a, b) => a - b);
    const tempRange = (sortedTemps[sortedTemps.length - 1] || 0) - (sortedTemps[0] || 0);
    const tempIncrement = tempRange === 0 ? 1 : (tempRange / 4) % 5 !== 0 ? tempRange / 4 : (tempRange / 4) * 1.25;
    const firstTick = (sortedTemps[0] || 0) - tempIncrement * 0.5;
    const tickValues = Array.from({ length: 6 }, (_, i) => Math.ceil(firstTick + i * tempIncrement));

    minV = Math.min(minV, ...tickValues);
    maxV = Math.max(maxV, ...tickValues);

    const minT = timestamps[0];
    const maxT = timestamps[timestamps.length - 1];
    const rangeV = maxV - minV || 1;
    const rangeT = maxT - minT || 1;
    const xOf = (t) => PLOT.left + ((t - minT) / rangeT) * plotW;
    const yOf = (v) => PLOT.top + (1 - (v - minV) / rangeV) * plotH;

    const builtLines = filledSeries
      .map((s) => {
        const pathPoints = s.points.filter(([, value]) => value != null);
        if (pathPoints.length < 2) return null;
        return {
          key: s.key,
          color: s.color,
          dashed: s.dashed,
          path: 'M' + pathPoints.map(([t, v]) => `${round1(xOf(t))},${round1(yOf(v))}`).join(' L'),
        };
      })
      .filter(Boolean);

    const ticks = [...tickValues].reverse().map((v) => ({
      label: String(v),
      y: round1(yOf(v)),
    }));

    const timeIncrement = rangeT / 5;
    const timeTicks = Array.from({ length: 6 }, (_, i) => {
      const target = minT + i * timeIncrement;
      return timestamps.find((time) => time >= target) ?? maxT;
    });
    const dateFormat = realTime ? 'HH:mm' : formatDateAxis(intervalUnit);
    const labels = timeTicks.map((time) => moment(time).format(dateFormat));

    return {
      lines: builtLines,
      setPointY: typeof setPoint === 'number' ? round1(yOf(setPoint)) : null,
      yTicks: ticks,
      timeLabels: labels,
      currentY: typeof currentTemp === 'number' ? round1(yOf(currentTemp)) : null,
    };
  }, [series, setPoint, currentTemp, isF, realTime, intervalUnit]);

  const bars = useMemo(() => {
    const points = (Array.isArray(activeBars.data) ? activeBars.data : [])
      .filter((d) => Array.isArray(d) && typeof d[0] === 'number' && typeof d[1] === 'number')
      .sort((a, b) => a[0] - b[0]);

    if (points.length === 0) return { rects: [], yTicks: [], timeLabels: [] };

    // consumption is a total, so the scale starts at zero however high the bars
    // run — otherwise a flat period would look like it swung wildly
    const maxV = Math.max(...points.map(([, value]) => value), 0);
    const top = maxV === 0 ? 1 : maxV;
    const tickValues = Array.from({ length: 6 }, (_, i) => (top / 5) * i);

    // leave a gap either side of each bar so neighbouring periods stay readable
    const band = plotW / points.length;
    const barW = band * 0.6;

    const rects = points.map(([time, value], i) => {
      const height = (value / top) * plotH;
      return {
        key: `${time}-${i}`,
        x: round1(PLOT.left + band * i + (band - barW) / 2),
        y: round1(PLOT.top + plotH - height),
        width: round1(barW),
        height: round1(Math.max(height, 0)),
      };
    });

    const dateFormat = formatDateAxis(intervalUnit);
    const labelStep = Math.max(1, Math.ceil(points.length / 6));

    return {
      rects,
      yTicks: [...tickValues].reverse().map((v) => ({
        label: String(Math.round(v * 10) / 10),
        y: round1(PLOT.top + (1 - v / top) * plotH),
      })),
      timeLabels: points
        .filter((_, i) => i % labelStep === 0)
        .map(([time]) => moment(time).format(dateFormat)),
    };
  }, [activeBars.data, intervalUnit]);

  const hasData = isBarGraph ? bars.rects.length > 0 : lines.length > 0;
  const activeTicks = isBarGraph ? bars.yTicks : yTicks;
  const activeTimeLabels = isBarGraph ? bars.timeLabels : timeLabels;
  const unit = isF ? '°F' : '°C';
  const curDisplay = currentTemp != null ? Math.round(currentTemp) : null;

  const handleSearch = () => onSearch?.({ graphType, startDate, endDate });
  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    onClear?.();
  };

  return (
    <Container>
      <Controls>
        <Field>
          <FieldLabel>SELECT GRAPH</FieldLabel>
          <Select value={graphType} onChange={(e) => setGraphType(e.target.value)}>
            {graphOptions(source).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
        <Field>
          <FieldLabel>SELECT START DATE</FieldLabel>
          <DateInput
            type="datetime-local"
            value={startDate}
            disabled={graphType === 'realtime'}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel>SELECT END DATE</FieldLabel>
          <DateInput
            type="datetime-local"
            value={endDate}
            disabled={graphType === 'realtime'}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Field>
        <ActionBtn type="button" onClick={handleClear}>CLEAR</ActionBtn>
        <ActionBtn type="button" $primary onClick={handleSearch}>SEARCH</ActionBtn>
      </Controls>

      {/* the print button captures this region, so the controls stay outside it */}
      <ChartArea className={reportClass}>
        <ChartLabel>{title || systemLabel}</ChartLabel>
        <ChartContainer>
          {hasData && isBarGraph ? (
            <ChartSvg viewBox="0 0 100 100" preserveAspectRatio="none">
              {bars.yTicks.map((t, i) => (
                <line
                  key={`bg${i}`}
                  x1={PLOT.left}
                  y1={t.y}
                  x2={PLOT.right}
                  y2={t.y}
                  stroke="var(--switch-chart-grid)"
                  strokeWidth="0.5"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {bars.rects.map((r) => (
                <rect
                  key={r.key}
                  x={r.x}
                  y={r.y}
                  width={r.width}
                  height={r.height}
                  fill={source === 'gas' ? '#F97316' : '#00D492'}
                />
              ))}
            </ChartSvg>
          ) : hasData ? (
            <ChartSvg viewBox="0 0 100 100" preserveAspectRatio="none">

              {yTicks.map((t, i) => (
                <line
                  key={`g${i}`}
                  x1={PLOT.left}
                  y1={t.y}
                  x2={PLOT.right}
                  y2={t.y}
                  stroke="var(--switch-chart-grid)"
                  strokeWidth="0.5"
                  vectorEffect="non-scaling-stroke"
                />
              ))}

              {setPointY != null && (
                <line
                  x1={PLOT.left}
                  y1={setPointY}
                  x2={PLOT.right}
                  y2={setPointY}
                  stroke="#51A2FF"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                  vectorEffect="non-scaling-stroke"
                  opacity="0.8"
                />
              )}

              {currentY != null && (
                <line
                  x1={PLOT.left}
                  y1={currentY}
                  x2={PLOT.right}
                  y2={currentY}
                  stroke="#51A2FF"
                  strokeWidth="0.5"
                  vectorEffect="non-scaling-stroke"
                  opacity="0.3"
                />
              )}
              {lines.map((ln) => (
                <path
                  key={ln.key}
                  d={ln.path}
                  fill="none"
                  stroke={ln.color}
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  strokeDasharray={ln.dashed ? '4 2' : undefined}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </ChartSvg>
          ) : (
            <ChartPlaceholder>
              <PlaceholderLine />
              <PlaceholderText>
                {isBarGraph
                  ? 'No consumption for the selected dates'
                  : 'Temperature data loading...'}
              </PlaceholderText>
            </ChartPlaceholder>
          )}


          <YAxis>
            {(activeTicks.length ? activeTicks : [{ label: '--' }, { label: '--' }, { label: '--' }]).map((t, i) => (
              <YTick key={i} style={{ top: `${t.y != null ? t.y : i * 50}%` }}>{t.label}</YTick>
            ))}
          </YAxis>


          {!isBarGraph && curDisplay != null && currentY != null && (
            <CurrentTag style={{ top: `${currentY}%` }}>{curDisplay}{unit}</CurrentTag>
          )}


          <XAxis>
            {activeTimeLabels.length > 0 ? (
              activeTimeLabels.map((label, i) => <span key={i}>{label}</span>)
            ) : (
              <>
                <span>--:--</span>
                <span>--:--</span>
                <span>--:--</span>
                <span>--:--</span>
              </>
            )}
          </XAxis>
          <XAxisTitle>Time (s) →</XAxisTitle>
        </ChartContainer>

        {isBarGraph ? (
          <ChartLegend>
            <LegendItem>
              <LegendDot $color={source === 'gas' ? '#F97316' : '#00D492'} />
              <span>
                {graphType === 'data'
                  ? 'data consumption'
                  : `${source} consumption`}
                {activeBars.unit ? ` (${activeBars.unit})` : ''}
              </span>
            </LegendItem>
          </ChartLegend>
        ) : (
          <ChartLegend>
            <LegendItem>
              <LegendDot $color="#F97316" />
              <span>ht - heater temperature</span>
            </LegendItem>
            <LegendItem>
              <LegendDot $color="#00D492" />
              <span>et - enclosure temperature</span>
            </LegendItem>
            <LegendItem>
              <LegendDot $color="#2B95D2" />
              <span>ot - outside temperature</span>
            </LegendItem>
          </ChartLegend>
        )}
      </ChartArea>
    </Container>
  );
};

export default SwitchTempDisplay;

const Container = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Controls = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 0;
  min-width: 0;
`;

const FieldLabel = styled.span`
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--switch-text-faint);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Select = styled.select`
  height: 30px;
  width: 100%;
  min-width: 0;
  padding: 0 8px;
  border-radius: 8px;
  background: var(--switch-input-bg);
  border: 0.8px solid var(--border-input);
  color: var(--switch-text-secondary);
  font-size: 11px;
  font-family: Inter, sans-serif;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: var(--switch-blue-border);
  }
`;

const DateInput = styled.input`
  height: 30px;
  width: 100%;
  min-width: 0;
  padding: 0 6px;
  border-radius: 8px;
  background: var(--switch-input-bg);
  border: 0.8px solid var(--border-input);
  color: var(--switch-text-secondary);
  font-size: 10px;
  font-family: Inter, sans-serif;
  outline: none;
  color-scheme: dark;

  .light & {
    color-scheme: light;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  &:focus {
    border-color: var(--switch-blue-border);
  }
`;

const ActionBtn = styled.button`
  height: 30px;
  flex-shrink: 0;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  font-family: Inter, sans-serif;
  border: 0.8px solid ${({ $primary }) => ($primary ? 'var(--switch-blue-border)' : 'var(--border-input)')};
  background: ${({ $primary }) => ($primary ? 'var(--switch-blue-soft)' : 'var(--switch-input-bg)')};
  color: ${({ $primary }) => ($primary ? '#51A2FF' : 'var(--switch-text-muted)')};
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--switch-blue-border);
    color: ${({ $primary }) => ($primary ? '#51A2FF' : 'var(--switch-text-primary)')};
  }
`;

const ChartArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const ChartLabel = styled.span`
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #2b7fff;
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 180px;
  background: var(--switch-panel-bg);
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  border-radius: 16px;
  padding: 16px 16px 28px 34px;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ChartSvg = styled.svg`
  flex: 1;
  width: 100%;
  min-height: 150px;
  overflow: visible;
`;

const YAxis = styled.div`
  position: absolute;
  left: 0;
  top: 16px;
  bottom: 28px;
  width: 30px;
`;

const YTick = styled.span`
  position: absolute;
  right: 4px;
  transform: translateY(-50%);
  font-size: 10px;
  font-family: Consolas, monospace;
  color: #f97316;
  white-space: nowrap;
`;

const CurrentTag = styled.span`
  position: absolute;
  right: 6px;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 700;
  font-family: Consolas, monospace;
  color: #5b9bd5;
  background: var(--switch-panel-bg);
  padding: 0 2px;
  white-space: nowrap;
`;

const XAxis = styled.div`
  display: flex;
  justify-content: space-between;
  position: absolute;
  left: 34px;
  right: 16px;
  bottom: 10px;

  span {
    font-size: 8px;
    color: var(--switch-text-faint);
    letter-spacing: 0.3px;
  }
`;

const XAxisTitle = styled.span`
  position: absolute;
  right: 16px;
  bottom: 0;
  font-size: 8px;
  color: var(--switch-text-faint);
`;

const ChartPlaceholder = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const PlaceholderLine = styled.div`
  width: 80%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--switch-border-strong), transparent);
`;

const PlaceholderText = styled.span`
  font-size: 10px;
  color: var(--switch-text-faint);
  letter-spacing: 0.5px;
`;

const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 6px 16px;
  background: var(--switch-panel-bg);
  border: 0.8px solid rgba(29, 41, 61, 0.5);
  border-radius: 100px;
  align-self: center;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  span {
    font-size: 9px;
    color: var(--switch-text-muted);
    letter-spacing: 0.3px;
  }
`;

const LegendDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;
